import React, {ReactElement} from 'react';
import {makeStyles, ListItemText} from '@material-ui/core';

import {useTranslation} from 'react-i18next';
import {
  APP_PRIMARY_COLOR,
  BORDER_COLOR,
  DEFAULT_BLACK_COLOR,
  DEFAULT_WHITE_COLOR
} from 'src/style/variable';
import {getChargeTypeLabel} from '@shared/constants/Mapping';
import HelperUtils, {sortByPriority} from '@shared/utils/HelperUtils';

const confirmStyles = makeStyles(() => ({
  root: {
    '& .MuiDialog-paperWidthSm': {
      //   width: '35rem'
    },
    '& .MuiDialog-paperFullWidth': {
      //   width: '35rem'
    }
  },
  menu: {
    '& li': {
      borderBottom: '1px solid #eeeeee'
    },
    '& li:last-child': {
      borderBottom: 'none'
    }
  },
  secondary: {
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      overflow: 'hidden',
      width: '100%'
    }
  },

  primary: {
    '& .MuiListItemText-primary': {
      fontSize: '1.1rem',
      overflow: 'hidden',
      width: '100%'
    }
  },
  totalChargeLabel: {
    '& .MuiListItemText-primary': {
      fontWeight: 600
    }
  },
  flex: {
    flex: 1
  },

  dropDownWidth: {
    minWidth: '15rem'
  },
  text: {
    fontSize: '1.3rem'
  },
  confirmButton: {
    height: '3rem',
    width: '5rem',
    color: DEFAULT_WHITE_COLOR,
    borderRadius: '4pt',
    fontSize: '1.2rem',
    textTransform: 'capitalize',
    backgroundColor: APP_PRIMARY_COLOR,
    '&:hover': {
      backgroundColor: APP_PRIMARY_COLOR
    }
  },
  cancelButton: {
    height: '3rem',
    width: '5rem',
    color: DEFAULT_BLACK_COLOR,
    borderRadius: '4pt',
    fontSize: '1.2rem',
    textTransform: 'capitalize'
  },
  chargeView: {
    justifyContent: 'space-between',
    color: 'black',
    display: 'flex',
    fontSize: '1.1rem',
    alignItems: 'center'
  },
  totalChargeView: {
    justifyContent: 'space-between',
    color: 'black',
    display: 'flex',
    fontSize: '1.2rem',
    alignItems: 'center'
  },
  chargeContent: {
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  displayFlex: {
    display: 'flex'
  },
  breakLine: {
    whiteSpace: 'pre-line'
  },
  divider: {
    margin: '16px 0',
    borderTop: `1px solid ${BORDER_COLOR}`
  }
}));

const ConfirmationDetails = (props: any): ReactElement => {
  const {
    amount,
    statementBalance,
    chargeTypeList,
    totalDue,
    convenienceFee,
    totalChargeFee,
    clinicConvenienceFeeFlag,
    machinePayment
  } = props;

  const {t} = useTranslation();
  const classes = confirmStyles();

  const divideAmountForEachSection = (): any => {
    let originalAmount = amount;

    const eachChargeSection: any = [];

    // AMOUNT DIVIDING BASED ON PRIORITY ORDER;
    const orderList = sortByPriority(chargeTypeList);

    orderList.forEach((element: any) => {
      if (originalAmount > 0 && element?.balance_amount) {
        const obj: any = {
          label: t(getChargeTypeLabel(element?.charge_type)),
          description: element?.description
        };
        if (originalAmount >= element?.balance_amount) {
          obj.amount = element?.balance_amount;
          originalAmount -= element?.balance_amount;
        } else {
          obj.amount = originalAmount;
          originalAmount = 0;
        }
        eachChargeSection.push(obj);
      }
    });

    if (originalAmount > 0 && statementBalance) {
      const obj: any = {
        label: t('CHARGE_TYPES.STATEMENT_BALANCE'),
        description: ''
      };
      if (originalAmount >= statementBalance) {
        obj.amount = statementBalance;
        originalAmount -= statementBalance;
      } else {
        obj.amount = originalAmount;
        originalAmount = 0;
      }
      eachChargeSection.push(obj);
    }

    return eachChargeSection;
  };

  const renderBody = () => {
    const result = divideAmountForEachSection();
    return (
      <div className={classes.menu}>
        {!machinePayment && (
          <>
            {result?.map((charge: any, index: number) => {
              return (
                <div
                  key={index}
                  className={[
                    classes.chargeView,
                    classes.chargeContent,
                    classes.breakLine
                  ].join(' ')}>
                  <ListItemText
                    className={[classes.secondary, classes.primary].join(' ')}
                    primary={charge?.label ?? ''}
                    secondary={charge?.description ?? ''}
                  />
                  <strong>
                    {HelperUtils.formatCurrencyAmount(charge?.amount, true)}
                  </strong>
                </div>
              );
            })}
            {result?.length > 0 && <div className={classes.divider} />}
          </>
        )}

        <div
          className={[
            classes.chargeView,
            classes.chargeContent,
            classes.breakLine
          ].join(' ')}>
          <ListItemText
            data-test-id="pcl-payments-totalDue"
            className={[classes.secondary, classes.primary].join(' ')}
            primary={t('PAYMENTS_OPTIONS.TOTAL_DUE')}
            secondary={''}
          />
          {HelperUtils.formatCurrencyAmount(totalDue, true)}
        </div>

        {Boolean(clinicConvenienceFeeFlag) && (
          <div
            className={[
              classes.chargeView,
              classes.chargeContent,
              classes.breakLine
            ].join(' ')}>
            <ListItemText
              className={[classes.secondary, classes.primary].join(' ')}
              primary={t('PAYMENTS_OPTIONS.AMOUNT_ENTERED')}
              secondary={''}
            />
            {HelperUtils.formatCurrencyAmount(
              totalChargeFee - convenienceFee,
              true
            )}
          </div>
        )}
        {Boolean(clinicConvenienceFeeFlag) && (
          <div
            className={[
              classes.chargeView,
              classes.chargeContent,
              classes.breakLine
            ].join(' ')}>
            <ListItemText
              className={[classes.secondary, classes.primary].join(' ')}
              primary={t('PAYMENTS_OPTIONS.CONVENIENCE_FEE')}
              secondary={''}
            />
            {HelperUtils.formatCurrencyAmount(convenienceFee, true)}
          </div>
        )}

        <div
          className={[
            classes.totalChargeView,
            classes.chargeContent,
            classes.breakLine
          ].join(' ')}>
          <ListItemText
            data-test-id="pcl-payments-totalAmountCharge"
            className={[
              classes.secondary,
              classes.primary,
              classes.totalChargeLabel
            ].join(' ')}
            primary={t('PAYMENTS_OPTIONS.TOTAL_TO_BE_CHARGED')}
            secondary={''}
          />
          <strong>
            {HelperUtils.formatCurrencyAmount(totalChargeFee, true)}
          </strong>
        </div>
        {machinePayment && <div className={classes.divider} />}
      </div>
    );
  };

  return <div>{renderBody()}</div>;
};

export default ConfirmationDetails;
